import { useCallback, useEffect, useState } from 'react';
import { useForm as useReactHookForm } from 'react-hook-form';

export default (config = {}) => {
  const {
    onCancel: customOnCancel,
    onSubmit: customOnSubmit,
    defaultValues = {},
    validationSchema = undefined,
    clearOnSubmit = false,
    clearOnCancel = true,
    isEditing: customIsEditing = false,
    editInline: customEditInline = false,
  } = config;
  const defaultValuesString = JSON.stringify(defaultValues);
  const reactHookForm = useReactHookForm({
    defaultValues,
    validationSchema,
  });
  const { reset, formState = {}, getValues, handleSubmit } = reactHookForm;
  const [editInline, setEditInline] = useState(customEditInline || false);
  const [isEditing, setIsEditing] = useState(customIsEditing || false);

  // Need to read it once to enable state updates (https://react-hook-form.com/api#formState).
  // eslint-disable-next-line no-unused-vars
  const { dirty } = formState;

  const resetToDefault = useCallback(() => {
    reset(JSON.parse(defaultValuesString));
  }, [reset, defaultValuesString]);

  const isValid = useCallback(async () => {
    if (!validationSchema) {
      return true;
    }

    return validationSchema
      .validate(getValues())
      .then(() => true)
      .catch(() => false);
  }, [getValues, validationSchema]);

  const onSubmit = useCallback(async () => {
    const defaultFormValues = JSON.parse(defaultValuesString);
    const formIsValid = await isValid();

    if (customOnSubmit) {
      await customOnSubmit({ ...defaultFormValues, ...getValues({ nest: true }) }, formIsValid);
    }

    if (editInline) {
      setIsEditing(false);
    }

    if (clearOnSubmit) {
      resetToDefault();
    }
  }, [
    isValid,
    resetToDefault,
    getValues,
    clearOnSubmit,
    customOnSubmit,
    editInline,
    defaultValuesString,
  ]);

  const onCancel = useCallback(async () => {
    const defaultFormValues = JSON.parse(defaultValuesString);
    const formIsValid = await isValid();

    if (customOnCancel) {
      customOnCancel({ ...defaultFormValues, ...getValues() }, formIsValid);
    }

    if (clearOnCancel) {
      setIsEditing(false);
      resetToDefault();
    }
  }, [isValid, getValues, clearOnCancel, resetToDefault, customOnCancel, defaultValuesString]);

  useEffect(() => {
    resetToDefault();
  }, [resetToDefault]);

  useEffect(() => {
    setEditInline(customEditInline);
  }, [customEditInline]);

  useEffect(() => {
    setIsEditing(customIsEditing);
  }, [customIsEditing]);

  return {
    ...reactHookForm,
    defaultValues,
    isSubmitting: formState && formState.isSubmitting,
    submitted: formState && formState.isSubmitted,
    clear: reset,
    editInline,
    setEditInline,
    isEditing,
    setIsEditing,
    onSubmit: handleSubmit(onSubmit),
    onCancel,
    resetToDefault,
    isValid,
  };
};
